import { Alert, Button, Divider, Snackbar, Typography } from '@mui/material'
import '../../Misc/ActionBar.css'
import { ApprovalOutlined, BlockOutlined, DeleteOutlined, EditOutlined, IosShareOutlined } from '@mui/icons-material'
import { itesArLineType, sewpTrLineType } from 'src/types'
import axios from 'axios'
import { useState } from 'react'

export default function BulkEditActionBar({
  selectedItems,
  tab,
  contractNumber,
  reloadCatalog,
  setBulkEditOpen,
}: {
  selectedItems: (sewpTrLineType | itesArLineType)[]
  tab: string
  contractNumber: string
  reloadCatalog: () => void
  setBulkEditOpen: (open: boolean) => void
}) {
  const [trTxtDownloading, setTrTxtDownloading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null)
  const route = contractNumber.toLowerCase() === 'w819tc25da023' ? 'ar' : 'tr'

  const handleDownloadTrTxt = () => {
    setTrTxtDownloading(true)
    setErrorSnackbar(null)
    axios
      .get(`/catalog/${contractNumber}/tr/txt`, {
        params: {
          requestLineIds: selectedItems.map(item => item.RequestLineId),
        },
      })
      .then(res => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }))
        const a = document.createElement('a')
        a.href = url
        a.download = `${route}.txt`
        a.click()
        a.remove()
        URL.revokeObjectURL(url)

        reloadCatalog()
      })
      .catch(err => {
        setErrorSnackbar(err.response?.data.message ?? `Failed to download ${route.toUpperCase()} TXT file`)
        console.error('Failed to get technical request/approval request lines', err)
      })
      .finally(() => {
        setTrTxtDownloading(false)
      })
  }

  const handleDelete = () => {
    setDeleting(true)
    setErrorSnackbar(null)
    axios
      .delete(`/catalog/${contractNumber}/tr`, {
        data: {
          requestLineIds: selectedItems.map(item => item.RequestLineId),
        },
      })
      .then(() => {
        reloadCatalog()
      })
      .catch(err => {
        console.error(err.response?.data.error)
        setErrorSnackbar(
          err.response?.data.message ??
            `Failed to delete ${route === 'ar' ? 'approval request' : 'technical request'} line(s)`,
        )
      })
      .finally(() => {
        setDeleting(false)
      })
  }

  return (
    <>
      <div
        className={`action-bar${trTxtDownloading || deleting ? ' loading' : ''}`}
        style={{
          opacity: selectedItems.length > 0 ? 1 : 0,
          visibility: selectedItems.length > 0 ? 'visible' : 'hidden',
        }}
      >
        <Typography variant="h6">{selectedItems.length} selected</Typography>
        <Divider orientation="vertical" flexItem color="white" />
        {(tab === 'TRS (Awaiting)' || tab === 'ARS (Awaiting)') && (
          <>
            <Button variant="text" size="large" color="inherit" startIcon={<ApprovalOutlined />}>
              Approve
            </Button>
            <Button variant="text" size="large" color="inherit" startIcon={<BlockOutlined />}>
              Deny
            </Button>
          </>
        )}
        {(tab === 'TRS (Pending)' || tab === 'ARS (Pending)') && (
          <>
            <Button
              variant="text"
              size="large"
              color="inherit"
              startIcon={<EditOutlined />}
              onClick={() => setBulkEditOpen(true)}
              disabled={trTxtDownloading || deleting}
            >
              Bulk Edit
            </Button>
            <Button
              variant="text"
              size="large"
              color="inherit"
              startIcon={<IosShareOutlined />}
              onClick={handleDownloadTrTxt}
              loading={trTxtDownloading}
              disabled={deleting}
            >
              Export for {route.toUpperCase()}
            </Button>
            <Button
              variant="text"
              size="large"
              color="inherit"
              startIcon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleting}
              disabled={trTxtDownloading}
            >
              Delete
            </Button>
          </>
        )}
      </div>
      <Snackbar open={errorSnackbar !== null} onClose={() => setErrorSnackbar(null)}>
        <Alert severity="error" variant="filled">
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </>
  )
}
